import { memo, useMemo } from 'react';

import { motion } from 'framer-motion';

type BlobPosition =
  | { size: string; left: string; bottom: string }
  | { size: string; top: string; right: string };

const BLOB_STYLES = {
  backgroundImage: `
    repeating-linear-gradient(45deg, transparent 0 3px, rgba(0,0,0,0.2) 3px 4px),
    repeating-linear-gradient(-45deg, transparent 0 3px, rgba(0,0,0,0.2) 3px 4px),
    radial-gradient(ellipse 130% 130% at 20% 45%, rgba(0,14,239,0.35), rgba(0,14,239,0.08) 45%, transparent 75%)
  `,
  backgroundColor: 'rgba(255,255,255,0.98)',
  borderRadius: '55% 35% 65% 45% / 55% 35% 65% 45%'
} as const;

const BORDER_RADIUS_ANIMATION = [
  '55% 35% 65% 45% / 55% 35% 65% 45%',
  '48% 42% 58% 52% / 62% 28% 72% 38%',
  '62% 28% 72% 38% / 48% 42% 58% 52%',
  '55% 35% 65% 45% / 55% 35% 65% 45%'
] as const;

const SCALE_ANIMATION = [1, 1.04, 1.02, 1] as const;

const INTRO_MOBILE_BLOBS: BlobPosition[] = [
  { size: 'min(50vmin, 320px)', left: '5%', bottom: '16%' },
  { size: 'min(38vmin, 240px)', left: '60%', bottom: '24%' },
  { size: 'min(38vmin, 240px)', left: '35%', bottom: '25%' }
];

const INTRO_DESKTOP_BLOBS: BlobPosition[] = [
  { size: 'min(55vmin, 420px)', top: '20%', right: '26%' },
  { size: 'min(45vmin, 340px)', top: '46%', right: '14%' },
  { size: 'min(40vmin, 300px)', top: '18%', right: '4%' }
];

const LANDING_MOBILE_BLOBS: BlobPosition[] = [
  { size: 'min(55vmin, 400px)', left: '30%', bottom: '15%' }
];

const LANDING_DESKTOP_BLOBS: BlobPosition[] = [
  { size: 'min(55vmin, 620px)', top: '20%', right: '18%' }
];

interface BlobFallbackProps {
  isMobile: boolean;
  variant: 'intro' | 'landing';
  isPending?: boolean;
  className?: string;
}

const BlobFallback = memo(function BlobFallback({
  isMobile,
  variant,
  isPending = false,
  className = ''
}: BlobFallbackProps) {
  const blobs = useMemo(() => {
    if (variant === 'intro') {
      return isMobile ? INTRO_MOBILE_BLOBS : INTRO_DESKTOP_BLOBS;
    }

    return isMobile ? LANDING_MOBILE_BLOBS : LANDING_DESKTOP_BLOBS;
  }, [isMobile, variant]);

  const { duration, staggerDelay } = useMemo(() => {
    if (variant === 'landing' && isPending) {
      return { duration: 1.5, staggerDelay: 0.15 };
    }

    return { duration: 7, staggerDelay: 0.4 };
  }, [variant, isPending]);

  return (
    <div
      className={`w-full h-full relative overflow-hidden ${className}`.trim()}
    >
      {blobs.map((blob, i) => (
        <motion.div
          key={i}
          className="absolute shrink-0 overflow-hidden opacity-30 -translate-x-1/2 -translate-y-1/2 will-change-transform"
          style={{
            width: blob.size,
            height: blob.size,
            ...('left' in blob
              ? { left: blob.left, bottom: blob.bottom }
              : { top: blob.top, right: blob.right }),
            ...BLOB_STYLES
          }}
          animate={{
            borderRadius: [...BORDER_RADIUS_ANIMATION],
            scale: [...SCALE_ANIMATION]
          }}
          transition={{
            duration,
            repeat: Infinity,
            ease: [0.4, 0, 0.2, 1],
            delay: i * staggerDelay
          }}
        />
      ))}
    </div>
  );
});

export default BlobFallback;
